
import { Component } from "vue-property-decorator";
import { ProjectView } from "@/views/project-view";
import { ObjectHelper, QuickHelper } from "@root/core";
import { promoRedeemErrorMessageMap, promoRedeemFallbackKey } from "@/helpers/error-code";
import { resolveGlobalErrorMessage } from "@root/core/utilities";
import {
    translationJobAnalyzeErrorMessageMap,
    translationJobAnalyzeFallbackKey,
    translationJobErrorMessageMap,
    translationJobFallbackKey,
} from "@/helpers/error-code-translation-job";
import { extractErrorCode, resolveErrorMessageKey } from "@root/core/utilities";
import { ETranslationFromLang } from "@/helpers/enums";
import TranslationJobHeaderControls from "./components/translation-job-header-controls.vue";
import TranslationJobAnalyzeModal from "./components/translation-job-analyze-modal.vue";
import TranslationJobTopupModal from "./components/translation-job-topup-modal.vue";

type AnalysisResult = {
    tokens: number | null;
    words: number | null;
    pages: number | null;
    costPoint: number | null;
    raw: any[];
};

type OrganizationOption = {
    id: string;
    name: string;
    userId?: string | null;
    wallet?: {
        balance?: number | null;
    } | null;
    memberships?: Array<{
        role?: string | null;
    }>;
};

@Component({
    components: {
        "ivc-translation-job-header-controls": TranslationJobHeaderControls,
        "ivc-translation-job-analyze-modal": TranslationJobAnalyzeModal,
        "ivc-translation-job-topup-modal": TranslationJobTopupModal,
    },
})
export default class extends ProjectView {
    /// base
    quick = this.quicks().TranslationJob;

    showModal = false;
    step = 1;
    isAnalyzing = false;
    isCreating = false;
    isConverting = false;
    showTopupModal = false;
    topupTargetName = "-";
    isRedeeming = false;

    organizations: OrganizationOption[] = [];
    currentOrgId: string | null = null;
    currentUserId: string | null = null;
    sseSource: EventSource | null = null;
    sseReloadTimer: number | null = null;
    sseFlushTimer: number | null = null;
    ssePendingJobs: any[] = [];
    statusFilter = "all";

    form = {
        file: null as File | null,
        sourceLang: "auto",
        targetLang: "",
    };

    analysisResult: AnalysisResult | null = null;
    draftId: string | null = null;

    get ObjectHelper() {
        return ObjectHelper;
    }
    get QuickHelper() {
        return QuickHelper;
    }

    get tableParams() {
        const where: Record<string, any> = {};
        if (this.currentOrgId) {
            where.organizationId = this.currentOrgId;
        }
        if (this.statusFilter !== "all") {
            where.status = this.statusFilter;
        }
        return { where };
    }

    get tableBind() {
        return ObjectHelper.deepMerge(
            QuickHelper.forTableR(this.quick, null, this.tableParams, {
                enabled: !!this.currentOrgId,
            }),
        );
    }

    get currentOrg() {
        return this.organizations.find((org) => org.id === this.currentOrgId) ?? null;
    }

    get currentBalance() {
        return this.currentOrg?.wallet?.balance ?? 0;
    }

    get balanceAfter() {
        const cost = this.analysisResult?.costPoint ?? 0;
        return this.currentBalance - cost;
    }

    get infAnalyze() {
        return require("raw-loader!./interfaces/translation-job.analyze.interface")
            .default;
    }

    get languageLabels() {
        return ETranslationFromLang as Record<string, string>;
    }

    getLanguageLabel(code: string) {
        const key = this.languageLabels[code] ?? code;
        return this.$_(key);
    }

    async mounted() {
        await this.loadOrganizations();
        this.startSse();
    }

    beforeDestroy() {
        this.stopSse();
    }

    openModal() {
        this.step = 1;
        this.analysisResult = null;
        this.draftId = null;
        this.form = {
            file: null,
            sourceLang: "auto",
            targetLang: "",
        };
        this.showModal = true;
    }

    openTopupModal(payload?: { name?: string }) {
        const fallbackName = this.currentOrg?.name ?? "-";
        this.topupTargetName = payload?.name ?? fallbackName;
        this.showTopupModal = true;
    }

    closeModal() {
        this.showModal = false;
        this.step = 1;
        this.analysisResult = null;
        this.draftId = null;
    }

    prevStep() {
        this.step = 1;
    }

    async analyzeFile() {
        if (this.isAnalyzing) return;
        if (!this.form.file) return;

        const t0 = performance.now();
        this.isAnalyzing = true;
        this.isConverting = this.needsConvert(this.form.file);
        try {
            const draft = await this.requestDraftAnalyze(
                this.form.file,
                this.form.sourceLang,
                this.form.targetLang,
                this.currentOrgId,
            );
            const t1 = performance.now();
            this.console.log(
                `[analyze] total client time=${Math.round(t1 - t0)}ms`,
            );
            this.draftId = draft?.id ?? null;
            this.analysisResult = this.parseDraftResult(draft);
            this.step = 2;
        } catch (error) {
            this.console.error(error);
            if ((error as any)?.handled) return;
            const code =
                error && typeof error === "object" && "code" in (error as any)
                    ? String((error as any).code)
                    : "";
            if (code === "TRANSLATION_JOB_ANALYZE_NO_TEXT") {
                this.$alert?.(
                    {
                        title: this.$_("w_TranslationJobAnalyzeNoTextTitle"),
                        message: this.$_("w_TranslationJobAnalyzeNoTextDesc"),
                    },
                    "warning",
                );
                (error as any).handled = true;
                return;
            }
            const messageKey = resolveErrorMessageKey(
                code,
                translationJobAnalyzeErrorMessageMap,
                translationJobAnalyzeFallbackKey,
            );
            this.$alert?.(
                {
                    title: this.$_("w_Error"),
                    message: this.$_(messageKey),
                },
                "danger",
            );
            (error as any).handled = true;
        } finally {
            this.isAnalyzing = false;
            this.isConverting = false;
        }
    }

    private needsConvert(file: File) {
        const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
        return ext === "doc" || ext === "xls" || ext === "ppt";
    }

    confirmAndNext() {
        return this.finalizeDraft({ keepOpen: true });
    }

    async confirmTranslation() {
        return this.finalizeDraft({ keepOpen: false });
    }

    async finalizeDraft({ keepOpen }: { keepOpen: boolean }) {
        if (this.isCreating || !this.draftId) return;
        this.isCreating = true;
        try {
            await this.requestDraftConfirm(this.draftId);
            this.applyWalletDebit();
            this.reloadJobTable();
            await this.loadOrganizations();
            if (keepOpen) {
                const lastTargetLang = this.form.targetLang;
                this.step = 1;
                this.analysisResult = null;
                this.draftId = null;
                this.form = {
                    file: null,
                    sourceLang: "auto",
                    targetLang: lastTargetLang,
                };
            } else {
                this.closeModal();
            }
        } catch (error) {
            this.console.error(error);
            if ((error as any)?.handled) return;
            const code = error instanceof Error ? (error as any).code : "";
            const messageKey = resolveErrorMessageKey(
                code,
                translationJobErrorMessageMap,
                translationJobFallbackKey,
            );
            this.$alert?.(
                {
                    title: this.$_("w_Error"),
                    message: this.$_(messageKey),
                },
                "danger",
            );
            (error as any).handled = true;
            if (code === "TRANSLATION_JOB_INSUFFICIENT_BALANCE") {
                this.openTopupModal();
            }
        } finally {
            this.isCreating = false;
        }
    }

    async requestDraftAnalyze(
        file: File,
        sourceLang: string,
        targetLang: string,
        organizationId?: string | null,
    ) {
        const t0 = performance.now();
        const formData = new FormData();
        formData.append("file", file, file.name);
        if (sourceLang) formData.append("sourceLang", sourceLang);
        if (targetLang) formData.append("targetLang", targetLang);
        if (organizationId) formData.append("organizationId", organizationId);

        try {
            const result = await this.$server.C(
                "translation-job-draft/analyze",
                formData as any,
            );
            const t1 = performance.now();
            this.console.log(
                `[analyze] request time=${Math.round(t1 - t0)}ms`,
            );
            return result;
        } catch (error) {
            const t1 = performance.now();
            this.console.log(
                `[analyze] request failed after=${Math.round(t1 - t0)}ms`,
            );
            const code = extractErrorCode((error as any)?.body ?? error);
            (error as any).code = code || "Draft analyze request failed";
            throw error;
        }
    }

    async requestDraftConfirm(id: string) {
        try {
            return await this.$server.C("translation-job-draft/confirm", {
                id,
            });
        } catch (error) {
            const code = extractErrorCode((error as any)?.body ?? error);
            (error as any).code = code || "Draft confirm request failed";
            throw error;
        }
    }

    parseDraftResult(payload: any): AnalysisResult {
        const result: AnalysisResult = {
            tokens: payload?.tokensInTotal ?? null,
            words: payload?.wordsTotal ?? null,
            pages: payload?.pagesTotal ?? null,
            costPoint: payload?.expectedDebit ?? null,
            raw: payload?.calculatorOutput?.json ?? [],
        };
        const normalize = (value: any) => {
            if (value == null) return null;
            if (typeof value === "number" && !Number.isNaN(value)) return value;
            if (typeof value === "string") {
                const cleaned = value.replace(/,/g, "");
                const parsed = Number(cleaned);
                return Number.isNaN(parsed) ? null : parsed;
            }
            return null;
        };

        const mapMetric = (key: string, value: any) => {
            const metric = normalize(value);
            if (metric == null) return;
            const lower = key.toLowerCase();
            if (result.tokens == null && lower.includes("token")) {
                result.tokens = metric;
            } else if (
                result.words == null &&
                (lower.includes("word") || lower.includes("char"))
            ) {
                result.words = metric;
            } else if (result.pages == null && lower.includes("page")) {
                result.pages = metric;
            } else if (
                result.costPoint == null &&
                (lower.includes("point") || lower.includes("cost") || lower.includes("price"))
            ) {
                result.costPoint = metric;
            }
        };

        result.raw.forEach((item) => {
            if (!item || typeof item !== "object") return;
            if ("name" in item && "value" in item) {
                mapMetric(String(item.name), (item as any).value);
                return;
            }
            Object.entries(item).forEach(([key, value]) => mapMetric(key, value));
        });

        if (payload?.calculatorOutput?.total_pages != null) {
            result.pages =
                normalize(payload.calculatorOutput.total_pages) ?? result.pages;
        }

        return result;
    }

    async loadOrganizations() {
        const userId = this.resolveUserId();
        if (!userId) return;
        this.currentUserId = userId;

        const body = {
            where: {
                OR: [
                    { userId: { equals: userId } },
                    { memberships: { some: { userId: { equals: userId } } } },
                ],
            },
            include: {
                wallet: true,
                memberships: {
                    where: { userId: { equals: userId } },
                    select: { role: true },
                },
            },
            orderBy: [{ createdAt: "asc" as const }],
        };
        try {
            const payload = await this.$server.R("organization", body);
            const results = payload?.results ?? payload ?? [];
            this.organizations = Array.isArray(results) ? results : [];
        } catch (error) {
            this.console.error("Load organizations failed", error);
            if ((error as any)?.handled) return;
            return;
        }

        if (!this.currentOrgId && this.organizations.length > 0) {
            this.currentOrgId = this.organizations[0].id;
            this.reloadJobTable();
        }
        this.startSse();
    }

    selectOrganization(id: string) {
        if (this.currentOrgId === id) return;
        this.currentOrgId = id;
        this.reloadJobTable();
        this.startSse();
        if (this.showModal) {
            this.analysisResult = null;
            this.draftId = null;
            this.step = 1;
        }
    }

    private reloadJobTable() {
        const tableRef = this.ref("job/R").ref as any;
        tableRef?.reload?.();
    }

    private startSse() {
        if (!this.currentOrgId) return;
        const token = this.resolveAccessToken();
        if (!token) return;

        this.stopSse();

        const baseUrl = this.$server
            .Url("translation-job/stream" as any)
            .replace(/\?$/, "");
        const endpoint = new URL(baseUrl, window.location.origin);
        endpoint.searchParams.set("organizationId", this.currentOrgId);
        endpoint.searchParams.set("token", token);

        this.sseSource = new EventSource(endpoint.toString());
        this.sseSource.addEventListener("job", (event) => {
            this.handleSseJobMessage(event as MessageEvent);
        });
        this.sseSource.addEventListener("error", () => {
            if (!this.sseSource) return;
        });
    }

    private stopSse() {
        if (this.sseSource) {
            this.sseSource.close();
            this.sseSource = null;
        }
        if (this.sseReloadTimer) {
            window.clearTimeout(this.sseReloadTimer);
            this.sseReloadTimer = null;
        }
        if (this.sseFlushTimer) {
            window.clearTimeout(this.sseFlushTimer);
            this.sseFlushTimer = null;
        }
        this.ssePendingJobs = [];
    }

    private scheduleJobReload() {
        if (this.sseReloadTimer != null) return;
        this.sseReloadTimer = window.setTimeout(() => {
            this.sseReloadTimer = null;
            this.reloadJobTable();
        }, 400);
    }

    private handleSseJobMessage(event: MessageEvent) {
        let payload: any = null;
        try {
            payload = event?.data ? JSON.parse(event.data) : null;
        } catch {
            this.scheduleJobReload();
            return;
        }
        const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
        if (jobs.length === 0) return;

        this.ssePendingJobs.push(...jobs);
        this.scheduleSseFlush();
    }

    private scheduleSseFlush() {
        if (this.sseFlushTimer != null) return;
        this.sseFlushTimer = window.setTimeout(() => {
            this.sseFlushTimer = null;
            this.flushSseJobs();
        }, 1000);
    }

    private flushSseJobs() {
        if (this.ssePendingJobs.length === 0) return;

        const tableRef = this.ref("job/R").ref as any;
        if (typeof tableRef?.updateQueryData !== "function") {
            this.ssePendingJobs = [];
            this.scheduleJobReload();
            return;
        }

        const merged = new Map<string, any>();
        for (const job of this.ssePendingJobs) {
            const id = String(job?.id ?? "");
            if (!id) continue;
            merged.set(id, job);
        }
        this.ssePendingJobs = [];

        const updates = Array.from(merged.values()).map((job) => ({
            id: job?.id,
            status: job?.status,
            progressDone: job?.progressDone,
            progressTotal: job?.progressTotal,
            progressPercent: job?.progressPercent,
            updatedAt: job?.updatedAt,
            outputFile: job?.outputFile ?? null,
        }));

        if (updates.length > 0) {
            tableRef.updateQueryData(updates);
        }

        if (updates.some((job) => String(job?.status ?? "") === "FAILED")) {
            this.loadOrganizations();
        }
    }

    private applyWalletDebit() {
        const cost = this.analysisResult?.costPoint ?? null;
        if (!cost || cost <= 0) return;
        if (!this.currentOrg?.wallet) return;
        this.currentOrg.wallet.balance = Math.max(
            0,
            (this.currentOrg.wallet.balance ?? 0) - cost,
        );
    }

    resolveUserId() {
        const token = this.resolveAccessToken();
        if (!token) return null;
        const payload = this.decodeJwtPayload(token);
        return payload?.sub ?? null;
    }

    private resolveAccessToken() {
        const headers = this.$server.applyAccessToken?.() ?? {};
        const rawAuth = headers.Authorization ?? "";
        const token = String(rawAuth).replace(/^Bearer\s+/i, "");
        return token || null;
    }

    decodeJwtPayload(token: string) {
        const parts = token.split(".");
        if (parts.length < 2) return null;
        try {
            const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
            const padded = base64.padEnd(
                base64.length + ((4 - (base64.length % 4)) % 4),
                "=",
            );
            const decoded = atob(padded);
            return JSON.parse(decoded);
        } catch (error) {
            this.console.warn("Failed to decode token", error);
            return null;
        }
    }

    get fileExtension() {
        return this.form.file?.name?.split(".").pop()?.toLowerCase() ?? "";
    }

    get fileIconWrapperClass() {
        if (this.fileExtension.includes("ppt")) return "icon-ppt";
        if (this.fileExtension.includes("xls")) return "icon-excel";
        return "icon-word";
    }

    get fileIconClass() {
        if (this.fileExtension.includes("ppt")) return "fa-file-powerpoint";
        if (this.fileExtension.includes("xls")) return "fa-file-excel";
        return "fa-file-word";
    }

    async redeemTopupCode(payload: { code?: string }) {
        if (this.isRedeeming) return;
        const code = payload?.code?.trim() ?? "";
        if (!code) {
            this.$alert?.(
                {
                    title: this.$_("w_Error"),
                    message: this.$_("w_TranslationJobTopupRedeemEmpty"),
                },
                "danger",
            );
            return;
        }

        this.isRedeeming = true;
        try {
            const result = await this.$server.C("promotion-code/redeem", {
                code,
                organizationId: this.currentOrgId ?? undefined,
            });
            const creditApplied = Number(result?.creditApplied ?? 0);
            this.$alert?.(
                {
                    title: this.$_("w_Success"),
                    message: this.$_("w_TranslationJobTopupRedeemSuccess", [
                        Math.ceil(creditApplied).toLocaleString(),
                    ]),
                },
                "success",
            );
            await this.loadOrganizations();
            this.showTopupModal = false;
        } catch (error) {
            this.console.error(error);
            if ((error as any)?.handled) return;
            const code = extractErrorCode((error as any)?.body ?? error) ?? "";
            const globalMessage = resolveGlobalErrorMessage(code, {
                uri: "promotion-code",
                method: "C",
            });
            if (globalMessage) {
                this.$alert?.(
                    {
                        title: this.$_("w_Error"),
                        message: this.$_(globalMessage.key, globalMessage.params),
                    },
                    "danger",
                );
                (error as any).handled = true;
                return;
            }
            const messageKey = resolveErrorMessageKey(
                code,
                promoRedeemErrorMessageMap,
                promoRedeemFallbackKey,
            );
            this.$alert?.(
                {
                    title: this.$_("w_Error"),
                    message: this.$_(messageKey),
                },
                "danger",
            );
            (error as any).handled = true;
        } finally {
            this.isRedeeming = false;
        }
    }

    async handleTopupSucceed(payload?: { amount?: number }) {
        if (payload?.amount && this.currentOrg?.wallet) {
            this.currentOrg.wallet.balance =
                (this.currentOrg.wallet.balance ?? 0) + Number(payload.amount);
        }
        await this.loadOrganizations();
        this.showTopupModal = false;
    }
}
